<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'
import { entryTypeLabel, formatNumber, monthValue, toDateInput } from '../utils/format'
import type { Employee, Option, TimesheetEntry, TimesheetEntryType } from '../types/hr'

const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const { listAllTimesheets, createTimesheet, deleteTimesheet, getEmployee, listWorkplaceOptions } = useHrApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('timesheets.manage'))

const today = toDateInput(new Date())

const entries = ref<TimesheetEntry[]>([])
const departments = ref<Option[]>([])
const month = ref(monthValue(today))
const departmentId = ref<string | number>('')
const standardDay = ref<number | string>(8)
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showForm = ref(false)
const showEmployeePicker = ref(false)
const selectedEmployee = ref<Employee | null>(null)

const form = reactive({
  employee_id: null as number | null,
  work_date: today,
  entry_type: 'office_task' as TimesheetEntryType,
  task_name: '',
  hours_worked: '' as number | string,
  cycles_completed: '' as number | string,
  notes: '',
})

function rangeFor(value: string): { from: string; to: string } {
  const [year, monthPart] = value.split('-').map(Number)
  if (!year || !monthPart) return { from: today, to: today }
  const lastDay = new Date(year, monthPart, 0).getDate()
  return { from: `${value}-01`, to: `${value}-${String(lastDay).padStart(2, '0')}` }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const range = rangeFor(month.value)
    entries.value = await listAllTimesheets({
      date_from: range.from,
      date_to: range.to,
      department_agent_id: departmentId.value || undefined,
    })
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

onMounted(() => {
  void loadDepartments()
  void load()
  void applyQueryPrefill()
})

watch([month, departmentId], () => {
  void load()
})

const threshold = computed(() => {
  const value = Number(standardDay.value)
  return Number.isFinite(value) && value > 0 ? value : 8
})

interface DayRow {
  key: string
  employeeId: number
  employeeName: string
  department: string
  date: string
  entries: number
  hours: number
  overtime: number
  amount: number
  currency: string
}

const dayRows = computed<DayRow[]>(() => {
  const map = new Map<string, DayRow & { tasks: Set<string> }>()
  for (const entry of entries.value) {
    const key = `${entry.employee_id}:${entry.work_date}`
    const row = map.get(key) ?? {
      key,
      employeeId: entry.employee_id,
      employeeName: entry.full_name,
      department: entry.department_name || '—',
      date: entry.work_date,
      entries: 0,
      hours: 0,
      overtime: 0,
      amount: 0,
      currency: entry.currency_code || '',
      tasks: new Set<string>(),
    }
    row.entries += 1
    row.hours += entry.hours_worked
    row.amount += entry.bill_amount
    row.tasks.add(entry.task_name)
    map.set(key, row)
  }

  return [...map.values()]
    .map((row) => ({ ...row, overtime: Math.max(0, row.hours - threshold.value) }))
    .sort((a, b) => b.date.localeCompare(a.date) || a.employeeName.localeCompare(b.employeeName))
})

const overtimeRows = computed(() => dayRows.value.filter((row) => row.overtime > 0))

const perEmployee = computed(() => {
  const map = new Map<number, { employeeId: number; name: string; department: string; hours: number; overtime: number; days: number; amount: number; currency: string }>()
  for (const row of dayRows.value) {
    const entry = map.get(row.employeeId) ?? {
      employeeId: row.employeeId,
      name: row.employeeName,
      department: row.department,
      hours: 0,
      overtime: 0,
      days: 0,
      amount: 0,
      currency: row.currency,
    }
    entry.hours += row.hours
    entry.overtime += row.overtime
    entry.amount += row.amount
    if (row.overtime > 0) entry.days += 1
    map.set(row.employeeId, entry)
  }
  return [...map.values()].sort((a, b) => b.overtime - a.overtime || b.hours - a.hours)
})

const totalHours = computed(() => dayRows.value.reduce((sum, row) => sum + row.hours, 0))
const totalOvertime = computed(() => overtimeRows.value.reduce((sum, row) => sum + row.overtime, 0))
const employeesWithOvertime = computed(() => new Set(overtimeRows.value.map((row) => row.employeeId)).size)

const recentEntries = computed(() =>
  [...entries.value]
    .sort((a, b) => (b.work_date + b.id).localeCompare(a.work_date + a.id))
    .slice(0, 12),
)

function openForm() {
  actionError.value = ''
  actionMessage.value = ''
  selectedEmployee.value = null
  form.employee_id = null
  form.work_date = rangeFor(month.value).to > today ? today : rangeFor(month.value).to
  form.entry_type = 'office_task'
  form.task_name = ''
  form.hours_worked = ''
  form.cycles_completed = ''
  form.notes = ''
  showForm.value = true
}

/**
 * Deep link from the employee profile: opens the work-hour form with the
 * employee already selected (`/overtime?employee=42&action=new`).
 */
async function applyQueryPrefill() {
  const employeeId = Number(route.query.employee ?? '')
  if (!employeeId || route.query.action !== 'new' || !canManage.value) return
  openForm()
  try {
    const employee = await getEmployee(employeeId)
    selectedEmployee.value = employee
    form.employee_id = employee.id
  } catch {
    // Keep the form open without a preselected employee.
  }
}

function selectEmployee(employee: Employee) {
  selectedEmployee.value = employee
  form.employee_id = employee.id
  showEmployeePicker.value = false
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (!form.employee_id) {
    actionError.value = t('hr.overtime.invalidEmployee')
    return
  }
  if (!form.work_date) {
    actionError.value = t('hr.overtime.invalidDate')
    return
  }
  if (!form.task_name.trim()) {
    actionError.value = t('hr.overtime.invalidTask')
    return
  }
  if (form.hours_worked !== '' && Number(form.hours_worked) <= 0) {
    actionError.value = t('hr.overtime.invalidHours')
    return
  }

  submitting.value = true
  try {
    await createTimesheet({
      employee_id: form.employee_id,
      work_date: form.work_date,
      entry_type: form.entry_type,
      task_name: form.task_name.trim(),
      hours_worked: form.hours_worked === '' ? 0 : Number(form.hours_worked),
      cycles_completed: form.cycles_completed === '' ? 0 : Number(form.cycles_completed),
      notes: form.notes.trim() || null,
    })
    actionMessage.value = t('hr.overtime.created')
    showForm.value = false
    void load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function remove(entry: TimesheetEntry) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('hr.overtime.deleteConfirm'))) return
  try {
    await deleteTimesheet(entry.id)
    actionMessage.value = t('hr.overtime.deleted')
    void load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  }
}

function employeeName(employee: Employee): string {
  const value = `${employee.last_name ?? ''} ${employee.first_name ?? ''}`.trim()
  return value || employee.employee_code
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.overtime.title') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="month" class="date-input" type="month">
    <select v-model="departmentId" class="filter-select">
      <option value="">{{ t('hr.common.allDepartments') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">{{ department.name }}</option>
    </select>
    <select v-model="standardDay" class="filter-select" :title="t('hr.overtime.standardHint')">
      <option :value="6">6 {{ t('hr.overtime.hours') }}</option>
      <option :value="8">8 {{ t('hr.overtime.hours') }}</option>
      <option :value="10">10 {{ t('hr.overtime.hours') }}</option>
      <option :value="12">12 {{ t('hr.overtime.hours') }}</option>
    </select>
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" type="button" @click="openForm">
      {{ t('hr.overtime.logHours') }}
    </button>
  </div>

  <div class="summary-row">
    <div class="summary-card">
      <span class="summary-value">{{ formatNumber(totalHours, 1) }}</span>
      <span class="summary-label">{{ t('hr.overtime.totalHours') }}</span>
    </div>
    <div class="summary-card accent">
      <span class="summary-value">{{ formatNumber(totalOvertime, 1) }}</span>
      <span class="summary-label">{{ t('hr.overtime.overtimeHours') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ employeesWithOvertime }}</span>
      <span class="summary-label">{{ t('hr.overtime.employeesAffected') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ overtimeRows.length }}</span>
      <span class="summary-label">{{ t('hr.overtime.overtimeEntries') }}</span>
    </div>
  </div>

  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
  <p v-if="actionError" class="error-inline">{{ actionError }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('hr.overtime.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <div v-else-if="entries.length === 0" class="state-message"><p>{{ t('hr.overtime.empty') }}</p></div>

  <template v-else>
    <h3 class="section-title">{{ t('hr.overtime.ledgerTitle') }}</h3>
    <div v-if="overtimeRows.length === 0" class="panel">
      <span class="empty-inline">{{ t('hr.overtime.noOvertime') }}</span>
    </div>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.overtime.workDate') }}</th>
            <th>{{ t('hr.overtime.employee') }}</th>
            <th>{{ t('hr.overtime.department') }}</th>
            <th>{{ t('hr.overtime.entries') }}</th>
            <th>{{ t('hr.overtime.dayHours') }}</th>
            <th>{{ t('hr.overtime.overtime') }}</th>
            <th>{{ t('hr.overtime.billAmount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in overtimeRows" :key="row.key">
            <td class="cell-date">{{ formatDisplayDate(row.date) }}</td>
            <td class="cell-name">{{ row.employeeName }}</td>
            <td>{{ row.department }}</td>
            <td class="cell-num">{{ row.entries }}</td>
            <td class="cell-num">{{ formatNumber(row.hours, 2) }}</td>
            <td class="cell-num">
              <span class="status-badge status-warn">{{ formatNumber(row.overtime, 2) }}</span>
            </td>
            <td class="cell-num">{{ formatNumber(row.amount) }} {{ row.currency }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 class="section-title">{{ t('hr.overtime.summaryTitle') }}</h3>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.overtime.employee') }}</th>
            <th>{{ t('hr.overtime.department') }}</th>
            <th>{{ t('hr.overtime.totalHours') }}</th>
            <th>{{ t('hr.overtime.overtime') }}</th>
            <th>{{ t('hr.overtime.overtimeEntries') }}</th>
            <th>{{ t('hr.overtime.billAmount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in perEmployee" :key="row.employeeId">
            <td class="cell-name">{{ row.name }}</td>
            <td>{{ row.department }}</td>
            <td class="cell-num">{{ formatNumber(row.hours, 2) }}</td>
            <td class="cell-num">{{ formatNumber(row.overtime, 2) }}</td>
            <td class="cell-num">{{ row.days }}</td>
            <td class="cell-num">{{ formatNumber(row.amount) }} {{ row.currency }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 class="section-title">{{ t('hr.overtime.entriesTitle') }}</h3>
    <div v-if="recentEntries.length === 0" class="panel">
      <span class="empty-inline">{{ t('hr.overtime.noEntries') }}</span>
    </div>
    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.overtime.workDate') }}</th>
            <th>{{ t('hr.overtime.employee') }}</th>
            <th>{{ t('hr.overtime.task') }}</th>
            <th>{{ t('hr.overtime.entryType') }}</th>
            <th>{{ t('hr.common.hours') }}</th>
            <th>{{ t('hr.common.cycles') }}</th>
            <th>{{ t('hr.overtime.billAmount') }}</th>
            <th v-if="canManage" class="cell-actions">{{ t('hr.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in recentEntries" :key="entry.id">
            <td class="cell-date">{{ formatDisplayDate(entry.work_date) }}</td>
            <td class="cell-name">
              {{ entry.full_name }}
              <small class="cell-sub">{{ entry.employee_code }}</small>
            </td>
            <td>{{ entry.task_name }}</td>
            <td>{{ entryTypeLabel(t, entry.entry_type) }}</td>
            <td class="cell-num">{{ formatNumber(entry.hours_worked, 2) }}</td>
            <td class="cell-num">{{ formatNumber(entry.cycles_completed, 2) }}</td>
            <td class="cell-num">{{ formatNumber(entry.bill_amount) }} {{ entry.currency_code ?? '' }}</td>
            <td v-if="canManage" class="cell-actions">
              <button class="link-action danger" type="button" @click="remove(entry)">
                {{ t('hr.common.delete') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>

  <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
    <form class="modal" @submit.prevent="submit">
      <h3>{{ t('hr.overtime.logTitle') }}</h3>
      <div class="form-grid tight">
        <div class="field field-wide">
          <label>{{ t('hr.overtime.employee') }}</label>
          <button class="field-button" type="button" @click="showEmployeePicker = true">
            <span :class="{ placeholder: !selectedEmployee }">
              {{ selectedEmployee ? employeeName(selectedEmployee) : t('hr.overtime.invalidEmployee') }}
            </span>
            <span aria-hidden="true">▾</span>
          </button>
        </div>
        <div class="field">
          <label for="timesheet-date">{{ t('hr.overtime.workDate') }}</label>
          <input id="timesheet-date" v-model="form.work_date" type="date" required>
        </div>
        <div class="field">
          <label for="timesheet-entry-type">{{ t('hr.overtime.entryType') }}</label>
          <select id="timesheet-entry-type" v-model="form.entry_type">
            <option value="office_task">{{ t('hr.entryType.office_task') }}</option>
            <option value="production_cycle">{{ t('hr.entryType.production_cycle') }}</option>
            <option value="admin_task">{{ t('hr.entryType.admin_task') }}</option>
          </select>
        </div>
        <div class="field field-wide">
          <label for="timesheet-task">{{ t('hr.overtime.task') }}</label>
          <input id="timesheet-task" v-model="form.task_name" type="text" required>
        </div>
        <div class="field">
          <label for="timesheet-hours">{{ t('hr.common.hours') }}</label>
          <input id="timesheet-hours" v-model="form.hours_worked" type="number" min="0" step="0.25">
        </div>
        <div class="field">
          <label for="timesheet-cycles">{{ t('hr.common.cycles') }}</label>
          <input id="timesheet-cycles" v-model="form.cycles_completed" type="number" min="0" step="1">
        </div>
        <div class="field field-wide">
          <label for="timesheet-notes">{{ t('hr.common.notes') }}</label>
          <textarea id="timesheet-notes" v-model="form.notes" />
        </div>
      </div>

      <p v-if="actionError" class="error-inline">{{ actionError }}</p>

      <div class="modal-actions">
        <button class="btn-secondary" type="button" @click="showForm = false">{{ t('hr.common.cancel') }}</button>
        <button class="btn-primary" type="submit" :disabled="submitting">
          {{ submitting ? t('hr.common.saving') : t('hr.common.save') }}
        </button>
      </div>
    </form>
  </div>

  <EmployeePickerModal
    :open="showEmployeePicker"
    @close="showEmployeePicker = false"
    @select="selectEmployee"
  />
</template>

<style scoped>
.section-title { margin: 24px 0 10px; font-size: 14px; font-weight: 700; color: #2b2027; }
</style>
